import dayjs from 'dayjs'
import i18next from 'i18next'
import { isEqual } from 'lodash'
import { action, computed, makeAutoObservable, observable } from 'mobx'
import moment from 'moment'

import { IRootStore } from 'store/types'
import { PickPointFilesViewModel } from 'sections/LKSection/shared/PickPointSharedModule/models/PickPointFilesViewModel'
import { getTimetableModel } from 'sections/LKSection/shared/PickPointSharedModule/utils'
import { MessagesType } from 'components/ui/Chat/Chat'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { DATE_FORMAT_WITH_TIME } from 'shared/constants/values'

import { OrderKeys } from '../../data'
import { AddOrderPayment, OrderInitialStateType, OrderMessageDTO, OrderPaymentStatus, PwaEventType } from '../../types'
import { Order } from '../entity/Order'
import { SendByType } from '../entity/OrderComment/OrderComment'
import { OrderLocationViewModel } from '../models/OrderLocationViewModel'
import { OrderWorkConditionsViewModel } from '../models/OrderWorkConditionsViewModel'
import { ViewOrderApiStore } from './ViewOrderApiStore'

export class ViewOrderUiStore {
  @observable page = 1
  @observable pageSize = 20
  @observable pwaEvent: PwaEventType | null = null

  @observable selectedOrder: Order | null = null
  @observable orderPaymentInfo: AddOrderPayment | null = null
  @observable orderPaymentStatus: OrderPaymentStatus | null = null
  @observable messages: OrderMessageDTO[] = []
  @observable comment = ''

  /* редактируемые свойства */
  @observable location = new OrderLocationViewModel()
  @observable fileList = new PickPointFilesViewModel()
  @observable workConditions = new OrderWorkConditionsViewModel()

  @observable private readonly apiStore = new ViewOrderApiStore(this)

  @observable initialized = false
  initialState: OrderInitialStateType | null = null

  constructor(readonly rootStore: IRootStore) {
    makeAutoObservable(this)
  }

  @computed
  get orderStore() {
    return this.rootStore.orderStore
  }

  @computed
  get createOrderUiStore() {
    return this.rootStore.createOrderUiStore
  }

  @computed
  get eLockUiStore() {
    return this.rootStore.eLockUiStore
  }

  @computed
  get orderList() {
    return this.orderStore.list
  }

  @computed
  get loadingOrderList() {
    return this.orderStore.loading
  }

  @computed
  get createOrderApiStore() {
    return this.rootStore.createOrderUiStore.apiStore
  }

  get isNoChanges() {
    return isEqual(this.initialState, this.hashFormState)
  }

  @computed
  get lastComment() {
    if (this.selectedOrder) {
      const [comment] = this.selectedOrder.comments.sort((a, b) => b.date.getTime() - a.date.getTime())
      return comment
    }
  }

  @computed
  get formattedComments(): MessagesType[] {
    if (!this.selectedOrder) {
      return []
    }

    return this.selectedOrder.comments?.map((commentItem) => ({
      name:
        commentItem.sendBy === SendByType.owner
          ? this.rootStore.appStore.user.info.name || ''
          : i18next.t(getSharedKey('operator')),
      text: commentItem.comment,
      time: dayjs(commentItem.date).format(DATE_FORMAT_WITH_TIME),
      date: commentItem.date,
      me: commentItem.sendBy === SendByType.owner,
      files: [],
    }))
  }

  @computed
  get formattedMessages(): MessagesType[] {
    if (!this.selectedOrder) {
      return []
    }

    return (
      this.messages?.map((messageItem) => ({
        name: i18next.t(getSharedKey('operator')),
        text: messageItem.message,
        time: moment(messageItem.createdAt).format(DATE_FORMAT_WITH_TIME),
        date: new Date(messageItem.createdAt),
        me: false,
        photos: messageItem.fileUrl || [],
      })) ?? []
    )
  }

  @computed
  get allMessages(): MessagesType[] {
    return [...this.formattedComments, ...this.formattedMessages].sort((a, b) =>
      new Date(a.date).getTime() > new Date(b.date).getTime() ? 1 : -1,
    )
  }

  getOrder(orderId: number) {
    return this.orderStore.getEntity(orderId) ?? null
  }

  get hashFormState(): OrderInitialStateType {
    const location = this.location
    const fileList = this.fileList
    const workConditions = this.workConditions
    const timetable = workConditions.timetable

    return {
      description: location.description.trim(),
      courierWay: location.courierWay.trim(),
      lat: location.coordinates.latitude,
      lng: location.coordinates.longitude,
      timetableStart: timetable.timetableStart,
      timetableEnd: timetable.timetableEnd,
      timetableGeneral: timetable.timetableGeneral,
      area: workConditions.area,
      warehouseArea: workConditions.warehouseArea,
      dressingRoomsCount: workConditions.dressingRoomsCount,
      cellingHeight: workConditions.cellingHeight,
      videos: fileList.videos.map((video) => video.url),
      documents: fileList.documents.map((document) => document.url),
      photos: [
        { url: fileList.generalPhoto.url, isPublic: fileList.generalPhoto.isPublic },
        { url: fileList.nightlyGeneralPhoto.url, isPublic: fileList.nightlyGeneralPhoto.isPublic },
        { url: fileList.clientAreaPhoto.url, isPublic: fileList.clientAreaPhoto.isPublic },
        { url: fileList.fittingRoomsPhoto.url, isPublic: fileList.fittingRoomsPhoto.isPublic },
      ],
      comment: this.comment.trim(),
      typePointItem: this.location.pickPointType,
      subsidyTypeItem: this.location.subsidyHexTypeItem,
      couriersAccess: this.location.couriersAccess,
      weekDays: timetable.weekDays,
    }
  }

  @action
  setInitialState(value: OrderInitialStateType | null) {
    this.initialState = value
  }

  init(order: Order) {
    this.initLocation(order)
    this.initWorkConditions(order)
    this.initFiles(order)

    if (order.eLockInfo) {
      this.eLockUiStore.initialize(order.eLockInfo)
    }

    this.setInitialState(this.hashFormState)
    this.setInitialized(true)
  }

  initLocation(order: Order) {
    this.location.setPickPointType(order.typePoint)
    this.location.setCityItem({
      id: order.cityId,
      name: order.district,
      prefix: order.cityName,
      formattedAddress: `${order.district}, ${order.cityName}`,
    })
    this.location.setStreetItem({
      street: order.street,
      house: order.house,
      lat: order.latitude,
      lng: order.longitude,
      precision: '',
      kind: '',
      postCode: 0,
      description: '',
      name: '',
    })
    this.location.setSubsidyHexTypeItem({
      value: order.subsidyHexType,
      label: i18next.t(`${OrderKeys.orderModule}.${order.subsidyHexType}`),
    })
    this.location.setDescription(order.description)
    this.location.setCourierWay(order.courierWay)
    this.location.setCoordinates({ latitude: order.latitude, longitude: order.longitude })
    this.location.setFloorLevel(order.floorLevel)
    this.location.setCouriersAccess(order.couriersAccess)
  }

  initWorkConditions(order: Order) {
    const timetableModel = getTimetableModel(order.timetable)
    const timetable = timetableModel.daily
    const days = timetableModel.week?.days

    const weekDays = this.workConditions.timetable.weekDays.map((day) => {
      const isDayOff = days?.[day.value].is_day_off ?? false
      return { ...day, isDayOff }
    })

    this.workConditions.timetable.setTimetableStart(timetable.open)
    this.workConditions.timetable.setTimetableEnd(timetable.close)
    this.workConditions.timetable.setTimetableGeneral(`${timetable.open}-${timetable.close}`)
    this.workConditions.timetable.setWeekDays(weekDays)
    this.workConditions.setArea(order.area.toString())
    this.workConditions.setWarehouseArea(order.warehouseArea.toString())
    this.workConditions.setDressRoomsCount(order.dressingRoomsCount.toString())
    this.workConditions.setCellingHeight(order.cellingHeight.toString())
  }

  initFiles(order: Order) {
    if (order.videos?.length && order.videos[0]?.url) {
      this.fileList.setVideoURL(order.videos[0].url)
    }

    this.fileList.setPhotos([...order.photos, ...order.documents])
  }

  @action
  setPage(value: number) {
    this.page = value
  }

  @action
  setPageSize(value: number) {
    this.pageSize = value
  }

  @action
  setPwaEvent(value: PwaEventType) {
    this.pwaEvent = value
  }

  @action
  setSelectedOrder(value: Order | null) {
    this.selectedOrder = value

    if (this.selectedOrder) {
      this.init(this.selectedOrder)
    }

    if (value === null) {
      this.setInitialized(false)
    }
  }

  @action
  setOrderPaymentInfo(value: AddOrderPayment | null) {
    this.orderPaymentInfo = value
  }

  @action
  setOrderPaymentStatus(value: OrderPaymentStatus | null) {
    this.orderPaymentStatus = value
  }

  @action
  setMessages(value: OrderMessageDTO[]) {
    this.messages = value
  }

  @action
  setComment(value: string) {
    this.comment = value
  }

  @action
  setInitialized(value: boolean) {
    this.initialized = value
  }

  reset = () => {
    this.setInitialized(false)
    this.setSelectedOrder(null)
    this.setOrderPaymentInfo(null)
    this.setOrderPaymentStatus(null)
    this.setComment('')

    this.location.reset()
    this.workConditions.reset()
    this.fileList.reset()

    this.eLockUiStore.reset()
    this.createOrderUiStore.setTariff(0)
  }

  async getOrderList() {
    await this.orderStore.loadList({ page: this.page, pageSize: this.pageSize })
  }

  async updateOrder() {
    return await this.apiStore.updateOrder()
  }

  async deleteOrder(orderId: number) {
    return await this.apiStore.deleteOrder(orderId)
  }

  async addOrderPayment(orderId: number) {
    return await this.apiStore.addOrderPayment(orderId)
  }

  async checkOrderPayment(orderId: number) {
    return await this.apiStore.checkOrderPayment(orderId)
  }

  async getOrderMessages(id: number) {
    return await this.apiStore.getOrderMessages(id)
  }

  async confirmOrder(id: number, willOpen: boolean) {
    return await this.apiStore.confirmOrder(id, willOpen)
  }

  async assignReferralCode(id: number, referralCode: string) {
    return await this.apiStore.assignReferralCode(id, referralCode)
  }

  async getFurnitureOrderType(id: number) {
    return await this.apiStore.getFurnitureOrderType(id)
  }

  async getPaymentDeadline(id: number) {
    return await this.apiStore.getPaymentDeadline(id)
  }
}
